import {AbstractDialogComponent} from "../components/ha-dialog/abstract-dialog.component";
import {DialogCommand} from "../components/ha-dialog/dialog-command.interface";
import {DialogCommandName} from "../components/ha-dialog/dialog-command.enum";
import {UiHelper} from "./ui.helper";
import {get, translate} from "lit-translate";
import {html} from "lit";

export class DialogHelper {

    private static _openedDialog: AbstractDialogComponent | null = null;

    /**
     * Opens a dialog of the specified component type with an optional payload, ensuring that only one dialog
     * of the same type is open at a time. If a different dialog is already open, an informational notification
     * will be displayed instead.
     *
     * @param {string} componentName - The name of the dialog component to create and display.
     * @param {TPayload} [payload] - Optional data to be assigned to the dialog component.
     * @return {AbstractDialogComponent | null} The displayed dialog instance if successfully created or opened,
     *                                          otherwise null if the dialog already exists and is not of the specified type.
     */
    public static showDialog<TPayload>(componentName: string, payload?: TPayload | undefined): AbstractDialogComponent | null {

        // An unregistered name yields undefined here and must end in the same Error as a
        // registered element that is no dialog, not in a TypeError on `.prototype`.
        if (!(customElements.get(componentName)?.prototype instanceof AbstractDialogComponent)) {
            throw new Error(`Dialog ${componentName} is not a valid dialog component`);
        }

        let dialog: AbstractDialogComponent;
        if (!DialogHelper._openedDialog) {
            dialog = document.createElement(componentName) as AbstractDialogComponent;
            DialogHelper._openedDialog = dialog;
            DialogHelper._attachCloseListener(dialog);
            document.body.appendChild(dialog);
        } else {
            if (DialogHelper._openedDialog.tagName !== componentName.toUpperCase()) {
                UiHelper.notify({
                    title: get(`components.ha-dialog.error.already_opened.title`),
                    content: html`
                        <div>${translate(`components.ha-dialog.error.already_opened.content`)}</div>
                    `,
                    level: "info",
                    closable: true,
                });

                return null;
            }

            dialog = DialogHelper._openedDialog;
        }

        Object.assign(dialog, payload);
        (dialog as AbstractDialogComponent)!.open();

        return dialog;
    }

    public static closeDialog(componentName: string): void {

        if (this._openedDialog?.tagName !== componentName.toUpperCase()) {
            // Not the dialog this helper is tracking — either it was opened via
            // UiHelper.openDialogEntityEdit (which owns its own cleanup through
            // its dialog.message listener), or this helper already tore it down.
            // The latter makes the call idempotent, which matters because the
            // subclasses' onClickClose overrides call closeDialog directly AND
            // then dispatch CLOSE, so the listener below runs a second time.
            return;
        }

        this._openedDialog.remove();
        this._openedDialog = null;
    }

    /**
     * Binds the teardown that makes the dialog's own CLOSE command load-bearing.
     *
     * Without this, `dialog.message` had no subscriber on this path at all:
     * AbstractDialogComponent.dispatchCommand emits a CustomEvent with neither
     * `bubbles` nor `composed`, so only a listener on the element itself can
     * ever observe it. A dialog whose "save and close" merely dispatches CLOSE
     * would save and then sit there — and, worse, leave `_openedDialog` set,
     * making every later showDialog of any kind bail out with the "already
     * opened" notification until a page reload.
     *
     * Registered ONLY when the element is created, never in the reuse branch:
     * the listener belongs to this instance and survives as long as it does, so
     * re-registering on reuse would stack duplicates.
     *
     * Deliberately handles CLOSE/CANCEL only. None of the dialogs opened through
     * this helper dispatch OKAY — they persist themselves via the CommandBus —
     * so forwarding OKAY here would be speculative today and a double-save the
     * day one of them starts emitting it.
     */
    private static _attachCloseListener(dialog: AbstractDialogComponent): void {

        const listener = {
            handleEvent: (event: CustomEvent<{ value: DialogCommand<unknown> }>): void => {
                switch (event.detail.value.command) {
                    case DialogCommandName.CLOSE:
                    case DialogCommandName.CANCEL:
                        dialog.removeEventListener(`dialog.message`, listener);
                        DialogHelper.closeDialog(dialog.tagName);
                        return;
                }
            }
        };

        dialog.addEventListener(`dialog.message`, listener);
    }

}